/**
 * Script Loading Utilities for Dynamic Script Injection
 * 
 * Provides a promise-based API for loading external scripts dynamically
 * based on user consent preferences with performance optimizations
 */

import { logger } from './logger';

export interface ScriptAttributes {
  src: string;
  async?: boolean;
  defer?: boolean;
  type?: string;
  crossOrigin?: string;
  integrity?: string;
  id?: string;
  [key: string]: string | boolean | undefined;
}

interface ScriptLoadOptions {
  retries?: number;
  timeout?: number;
  useIdleCallback?: boolean;
}

/**
 * Dynamically load a script and return a promise
 * @param attributes Script attributes including src and optional properties
 * @param options Loading options with retry and timeout settings
 * @returns Promise that resolves when script loads successfully or rejects on error
 */
export const loadScript = (
  attributes: ScriptAttributes, 
  options: ScriptLoadOptions = {}
): Promise<void> => {
  const { retries = 2, timeout = 10000, useIdleCallback = false } = options;

  return new Promise((resolve, reject) => {
    // Check if script is already loaded by src or id
    const existingScript = attributes.id 
      ? document.getElementById(attributes.id)
      : document.querySelector(`script[src="${attributes.src}"]`);
    
    if (existingScript) {
      // Script already exists, resolve immediately
      resolve();
      return;
    }

    const attemptLoad = (attempt: number): void => {
      const script = document.createElement('script');
      let timeoutId: number | null = null;

      // Set up event handlers
      const onLoad = () => {
        cleanup();
        resolve();
      };

      const onError = () => {
        cleanup();
        
        if (attempt < retries) {
          // Retry loading after a delay
          const delay = Math.pow(2, attempt) * 1000; // Exponential backoff
          setTimeout(() => attemptLoad(attempt + 1), delay);
        } else {
          reject(new Error(`Failed to load script: ${attributes.src} after ${retries + 1} attempts`));
        }
      };

      const onTimeout = () => {
        cleanup();
        script.remove();
        
        if (attempt < retries) {
          const delay = Math.pow(2, attempt) * 1000;
          setTimeout(() => attemptLoad(attempt + 1), delay);
        } else {
          reject(new Error(`Script loading timeout: ${attributes.src}`));
        }
      };

      const cleanup = () => {
        script.removeEventListener('load', onLoad);
        script.removeEventListener('error', onError);
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };

      // Attach event listeners
      script.addEventListener('load', onLoad);
      script.addEventListener('error', onError);

      // Set timeout if specified
      if (timeout > 0) {
        timeoutId = window.setTimeout(onTimeout, timeout);
      }

      // Set all attributes
      Object.entries(attributes).forEach(([key, value]) => {
        if (value !== undefined) {
          if (typeof value === 'boolean') {
            if (value) {
              script.setAttribute(key, '');
            }
          } else {
            script.setAttribute(key, value);
          }
        }
      });

      // Inject script into document head
      if (useIdleCallback && window.requestIdleCallback) {
        window.requestIdleCallback(() => {
          document.head.appendChild(script);
        });
      } else {
        document.head.appendChild(script);
      }
    };

    // Start loading attempt
    attemptLoad(0);
  });
};

/**
 * Load Facebook Pixel with optimizations
 * @param pixelId Facebook Pixel ID
 * @returns Promise that resolves when Facebook Pixel is loaded and configured
 */
export const loadFacebookPixel = async (pixelId: string): Promise<void> => {
  try {
    // Check if Facebook Pixel is already loaded
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((window as any).fbq) {
      logger.info('Facebook Pixel already loaded');
      return;
    }

    // Initialize Facebook Pixel
    if (typeof window !== 'undefined') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const fbq = function(...args: any[]) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((fbq as any).callMethod) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (fbq as any).callMethod.apply(fbq, args);
        } else {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (fbq as any).queue.push(args);
        }
      };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (!(window as any).fbq) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).fbq = fbq;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (fbq as any).push = fbq;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (fbq as any).loaded = true;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (fbq as any).version = '2.0';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (fbq as any).queue = [];

      // Load the Facebook Pixel script with optimizations
      await loadScript({
        src: 'https://connect.facebook.net/en_US/fbevents.js',
        async: true,
        id: 'facebook-pixel',
      }, {
        retries: 2,
        timeout: 10000,
        useIdleCallback: true
      });

      // Initialize the pixel
      fbq('init', pixelId);
      fbq('track', 'PageView');
    }
  } catch (error) {
    logger.error('Failed to load Facebook Pixel:', error);
    throw error;
  }
};

/**
 * Preload a script resource without executing it
 * Useful for warming up the cache
 */
export const preloadScript = (src: string): void => {
  // Check if already preloaded
  if (document.querySelector(`link[rel="preload"][href="${src}"]`)) {
    return;
  }

  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'script';
  link.href = src;
  link.crossOrigin = 'anonymous';
  document.head.appendChild(link);
};

/**
 * Batch load multiple scripts with coordination
 */
export const loadScripts = async (
  scripts: Array<{ attributes: ScriptAttributes; options?: ScriptLoadOptions }>
): Promise<void[]> => {
  return Promise.all(
    scripts.map(({ attributes, options }) => loadScript(attributes, options))
  );
};

/**
 * Remove a script from the document
 * @param selector CSS selector or script element to remove
 */
export const removeScript = (selector: string | HTMLScriptElement): void => {
  try {
    const script = typeof selector === 'string' 
      ? document.querySelector(selector) as HTMLScriptElement
      : selector;
    
    if (script && script.parentNode) {
      script.parentNode.removeChild(script);
    }
  } catch (error) {
    logger.error('Failed to remove script:', error);
  }
};
